/**
 * 3D model import: common mesh formats through three.js loaders, STEP/IGES/
 * BREP through OpenCascade (occt-import-js, WebAssembly) and IFC through
 * web-ifc — both loaded only when such a file is picked. Everything ends up
 * as a three.js object, exported to one GLB (the format projects load).
 */

import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { ThreeMFLoader } from 'three/examples/jsm/loaders/3MFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';

export const MODEL_EXTENSIONS = ['glb', 'gltf', 'obj', 'stl', 'fbx', '3mf', 'ply', 'step', 'stp', 'iges', 'igs', 'brep', 'ifc'] as const;
export type ModelExtension = (typeof MODEL_EXTENSIONS)[number];
export const MODEL_ACCEPT = MODEL_EXTENSIONS.map((e) => `.${e}`).join(',');

/** Over this many triangles projects slow down: the editor warns. */
export const TRIANGLE_WARNING = 200_000;
export const MAX_MODEL_BYTES = 100 * 1024 * 1024;

export interface LoadedModel {
  object: THREE.Object3D;
  /** The format's up axis is +Y (glTF, OBJ, FBX, PLY); the editor turns it to +Z. */
  yUp: boolean;
  triangles: number;
  bounds: THREE.Box3;
}

const DEFAULT_MATERIAL = (): THREE.MeshStandardMaterial => new THREE.MeshStandardMaterial({ color: 0xb8bcc2, roughness: 0.8, metalness: 0.05 });

function extensionOf(name: string): ModelExtension | null {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return MODEL_EXTENSIONS.find((known) => known === ext) ?? null;
}

export function isModelFile(name: string): boolean {
  return extensionOf(name) !== null;
}

function meshFromArrays(positions: ArrayLike<number>, indices: ArrayLike<number> | null, color: THREE.ColorRepresentation | null): THREE.Mesh {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(Array.from(positions), 3));
  if (indices) geometry.setIndex(Array.from(indices));
  geometry.computeVertexNormals();
  const material = DEFAULT_MATERIAL();
  if (color !== null) material.color = new THREE.Color(color);
  return new THREE.Mesh(geometry, material);
}

async function loadCad(bytes: Uint8Array, ext: 'step' | 'stp' | 'iges' | 'igs' | 'brep'): Promise<THREE.Object3D> {
  const [{ default: occtFactory }, { default: wasmUrl }] = await Promise.all([import('occt-import-js'), import('occt-import-js/dist/occt-import-js.wasm?url')]);
  const occt = await occtFactory({ locateFile: () => wasmUrl });
  const result = ext === 'brep' ? occt.ReadBrepFile(bytes, null) : ext === 'iges' || ext === 'igs' ? occt.ReadIgesFile(bytes, null) : occt.ReadStepFile(bytes, null);
  if (!result.success) throw new Error('the CAD file could not be read');
  const group = new THREE.Group();
  for (const mesh of result.meshes) {
    const color = mesh.color ? new THREE.Color(mesh.color[0], mesh.color[1], mesh.color[2]) : null;
    group.add(meshFromArrays(mesh.attributes.position.array, mesh.index?.array ?? null, color));
  }
  return group;
}

async function loadIfc(bytes: Uint8Array): Promise<THREE.Object3D> {
  const [WebIFC, { default: wasmUrl }] = await Promise.all([import('web-ifc'), import('web-ifc/web-ifc.wasm?url')]);
  const api = new WebIFC.IfcAPI();
  // Single-threaded build: the multi-threaded one needs cross-origin isolation.
  await api.Init(() => wasmUrl, true);
  const modelId = api.OpenModel(bytes);
  const group = new THREE.Group();
  try {
    api.StreamAllMeshes(modelId, (flatMesh) => {
      for (let i = 0; i < flatMesh.geometries.size(); i++) {
        const placed = flatMesh.geometries.get(i);
        const geometry = api.GetGeometry(modelId, placed.geometryExpressID);
        const vertices = api.GetVertexArray(geometry.GetVertexData(), geometry.GetVertexDataSize());
        const indices = api.GetIndexArray(geometry.GetIndexData(), geometry.GetIndexDataSize());
        // Vertex data is interleaved position(3) + normal(3).
        const positions = new Float32Array((vertices.length / 6) * 3);
        for (let v = 0; v < vertices.length / 6; v++) positions.set(vertices.subarray(v * 6, v * 6 + 3), v * 3);
        const mesh = meshFromArrays(positions, indices, new THREE.Color(placed.color.x, placed.color.y, placed.color.z));
        mesh.applyMatrix4(new THREE.Matrix4().fromArray(placed.flatTransformation));
        group.add(mesh);
        geometry.delete();
      }
    });
  } finally {
    api.CloseModel(modelId);
  }
  return group;
}

function countTriangles(object: THREE.Object3D): number {
  let triangles = 0;
  object.traverse((child) => {
    if (child instanceof THREE.Mesh && child.geometry instanceof THREE.BufferGeometry) {
      const g = child.geometry;
      triangles += (g.index ? g.index.count : (g.getAttribute('position')?.count ?? 0)) / 3;
    }
  });
  return Math.round(triangles);
}

/** Read a model file into a three.js object. Throws with a readable message. */
export async function loadModelFile(file: File): Promise<LoadedModel> {
  const ext = extensionOf(file.name);
  if (!ext) throw new Error('unsupported file type');
  if (file.size > MAX_MODEL_BYTES) throw new Error('file too large');
  const buffer = await file.arrayBuffer();
  let object: THREE.Object3D;
  let yUp = false;
  switch (ext) {
    case 'glb':
    case 'gltf':
      object = (await new GLTFLoader().parseAsync(ext === 'gltf' ? new TextDecoder().decode(buffer) : buffer, '')).scene;
      yUp = true;
      break;
    case 'obj':
      object = new OBJLoader().parse(new TextDecoder().decode(buffer));
      yUp = true;
      break;
    case 'fbx':
      object = new FBXLoader().parse(buffer, '');
      yUp = true;
      break;
    case 'ply': {
      const geometry = new PLYLoader().parse(buffer);
      geometry.computeVertexNormals();
      object = new THREE.Mesh(geometry, DEFAULT_MATERIAL());
      yUp = true;
      break;
    }
    case 'stl': {
      const geometry = new STLLoader().parse(buffer);
      geometry.computeVertexNormals();
      object = new THREE.Mesh(geometry, DEFAULT_MATERIAL());
      break;
    }
    case '3mf':
      object = new ThreeMFLoader().parse(buffer);
      break;
    case 'ifc':
      // web-ifc returns Y-up coordinates.
      object = await loadIfc(new Uint8Array(buffer));
      yUp = true;
      break;
    default:
      object = await loadCad(new Uint8Array(buffer), ext);
  }
  object.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(object);
  if (bounds.isEmpty()) throw new Error('the file holds no 3D geometry');
  return { object, yUp, triangles: countTriangles(object), bounds };
}

/** The model as one binary glTF — what gets stored and what projects load. */
export async function exportGlb(object: THREE.Object3D): Promise<Blob> {
  const result = await new GLTFExporter().parseAsync(object, { binary: true, onlyVisible: true });
  if (!(result instanceof ArrayBuffer)) throw new Error('export failed');
  return new Blob([result], { type: 'model/gltf-binary' });
}
